// src/components/home/DropSection.tsx
import React, { useLayoutEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowUpRight, Calendar, Sparkles, Tag } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

export type Drop = {
  DropId: number;
  Name: string;
  Story: string;
  ReleaseDate: string;
  IsLimited: boolean;
};

type Props = {
  drop: Drop;

  /** optional override strings */
  kicker?: string;              // "Current drop"
  limitedLabel?: string;        // "Limited edition"
  limitedBadgeLabel?: string;   // "Limited drop"
  primaryCtaLabel?: string;     // "Enter drop"
  secondaryCtaLabel?: string;   // "View collection"
  releaseLabel?: string;        // "Release"
  microLeft?: string;           // "Quiet luxury editorial"
  microRight?: string;          // "OWL DREAM"

  /** optional routes */
  primaryTo?: (d: Drop) => string;   // default `/drops/${DropId}`
  secondaryTo?: string;              // default "/collection"

  /** optional image */
  coverSrc?: string;                 // default "/images/drop-cover.jpg"
};

function usePrefersReducedMotion() {
  return useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  }, []);
}

export default function DropSection({
  drop,

  kicker,
  limitedLabel,
  limitedBadgeLabel,
  primaryCtaLabel,
  secondaryCtaLabel,
  releaseLabel,
  microLeft,
  microRight,

  primaryTo,
  secondaryTo = "/collection",
  coverSrc = "/images/drop-cover.jpg",
}: Props) {
  const { t, i18n } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();

  const rootRef = useRef<HTMLElement | null>(null);
  const leftRef = useRef<HTMLDivElement | null>(null);
  const rightRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useLayoutEffect(() => {
    if (!rootRef.current) return;
    if (reducedMotion) return;

    const ctx = gsap.context(() => {
      const left = leftRef.current;
      const right = rightRef.current;
      const img = imgRef.current;

      if (!left || !right) return;

      const bits = left.querySelectorAll<HTMLElement>("[data-od-reveal]");
      gsap.set(bits, { y: 14, opacity: 0 });
      if (img) gsap.set(img, { y: 16, opacity: 0 });
      gsap.set(right, { y: 10, opacity: 0 });

      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
        scrollTrigger: { trigger: rootRef.current, start: "top 78%", once: true },
      });

      tl.to(bits, { y: 0, opacity: 1, duration: 0.75, stagger: 0.08 })
        .to(img, { y: 0, opacity: 1, duration: 0.95 }, "-=0.55")
        .to(right, { y: 0, opacity: 1, duration: 0.7 }, "-=0.7");
    }, rootRef);

    return () => ctx.revert();
  }, [reducedMotion]);

  const locale = useMemo(() => {
    // i18next: "vi" | "en" ...
    const lng = (i18n.language || "en").toLowerCase();
    if (lng.startsWith("vi")) return "vi-VN";
    return "en-GB";
  }, [i18n.language]);

  const releaseText = useMemo(() => {
    const d = new Date(drop.ReleaseDate);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, [drop.ReleaseDate, locale]);

  const primaryHref = useMemo(() => {
    return primaryTo ? primaryTo(drop) : `/drops/${drop.DropId}`;
  }, [primaryTo, drop]);

  const finalKicker = kicker ?? t("dropSection.kicker");
  const finalLimited = limitedLabel ?? t("dropSection.limited");
  const finalLimitedBadge = limitedBadgeLabel ?? t("dropSection.limitedBadge");
  const finalPrimaryCta = primaryCtaLabel ?? t("dropSection.ctaPrimary");
  const finalSecondaryCta = secondaryCtaLabel ?? t("dropSection.ctaSecondary");
  const finalReleaseLabel = releaseLabel ?? t("dropSection.releaseLabel");
  const finalMicroLeft = microLeft ?? t("dropSection.microLeft");
  const finalMicroRight = microRight ?? t("dropSection.microRight");

  return (
    <section
      ref={rootRef}
      className="relative bg-brand-brown text-brand-ivory py-20 sm:py-24 lg:py-28 overflow-hidden"
    >
      {/* subtle background wash (ONLY brand colors) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-brown via-brand-brown to-brand-brown" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/12 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-bl from-brand-ivory/6 via-transparent to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT (text) */}
          <div ref={leftRef} className="lg:col-span-6">
            {/* kicker */}
            <div data-od-reveal className="inline-flex items-center gap-3">
              <span className="h-9 w-9 rounded-2xl border border-brand-ivory/12 bg-brand-ivory/5 grid place-items-center">
                <Sparkles className="w-4 h-4 text-brand-gold" />
              </span>
              <p className="text-[10px] uppercase tracking-[0.62em] text-brand-ivory/70">
                {finalKicker}
              </p>
            </div>

            {/* title */}
            <h2
              data-od-reveal
              className="mt-8 text-[30px] sm:text-[38px] lg:text-[46px] leading-[1.06]
                         uppercase tracking-[0.18em]"
            >
              {drop.Name}
            </h2>

            {/* story */}
            <p
              data-od-reveal
              className="mt-5 text-sm leading-[1.95] text-brand-ivory/70 max-w-xl"
            >
              {drop.Story}
            </p>

            {/* meta pills */}
            <div data-od-reveal className="mt-8 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-brand-ivory/12 bg-brand-ivory/5">
                <Calendar className="w-4 h-4 text-brand-ivory/70" />
                <span className="text-[10px] uppercase tracking-[0.55em] text-brand-ivory/70">
                  {releaseText}
                </span>
              </div>

              {drop.IsLimited && (
                <div className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-brand-gold/25 bg-brand-gold/10">
                  <Tag className="w-4 h-4 text-brand-gold" />
                  <span className="text-[10px] uppercase tracking-[0.55em] text-brand-gold">
                    {finalLimited}
                  </span>
                </div>
              )}
            </div>

            {/* CTA */}
            <div data-od-reveal className="mt-10">
              <Link
                to={primaryHref}
                className="inline-flex items-center justify-center gap-3 h-12 px-7 rounded-full
                           bg-brand-gold text-brand-brown
                           text-[10px] uppercase tracking-[0.55em]
                           hover:opacity-95 transition"
              >
                {finalPrimaryCta}
                <ArrowUpRight className="w-4 h-4" />
              </Link>

              <Link
                to={secondaryTo}
                className="ml-3 inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full
                           border border-brand-ivory/14 bg-transparent
                           text-[10px] uppercase tracking-[0.55em] text-brand-ivory/75
                           hover:text-brand-ivory hover:border-brand-ivory/25 hover:bg-brand-ivory/5 transition"
              >
                {finalSecondaryCta}
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* RIGHT (image card) */}
          <div ref={rightRef} className="lg:col-span-6">
            <div
              className="relative rounded-[28px] sm:rounded-[34px] overflow-hidden
                         border border-brand-ivory/12 bg-brand-ivory/5
                         shadow-[0_40px_120px_rgba(20,14,12,0.35)]"
            >
              <div className="relative aspect-[4/5] sm:aspect-[3/4] overflow-hidden">
                <img
                  ref={imgRef}
                  src={coverSrc}
                  alt={drop.Name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      "/images/placeholder.jpg";
                  }}
                />

                {/* subtle overlays */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-brown/65 via-transparent to-transparent" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-brand-gold/10 via-transparent to-transparent" />

                {/* corner badge */}
                {drop.IsLimited && (
                  <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full px-4 py-2
                                  border border-brand-ivory/14 bg-brand-brown/35 backdrop-blur-md">
                    <Tag className="w-4 h-4 text-brand-gold" />
                    <span className="text-[9px] uppercase tracking-[0.55em] text-brand-ivory/80">
                      {finalLimitedBadge}
                    </span>
                  </div>
                )}
              </div>

              {/* bottom meta strip */}
              <div className="px-5 sm:px-6 py-4 border-t border-brand-ivory/10 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[9px] uppercase tracking-[0.6em] text-brand-ivory/55">
                    {finalReleaseLabel}
                  </p>
                  <p className="mt-1 text-sm text-brand-ivory/85 truncate">
                    {releaseText}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="h-9 w-9 rounded-2xl border border-brand-ivory/12 bg-brand-ivory/5 grid place-items-center">
                    <ArrowUpRight className="w-4 h-4 text-brand-gold" />
                  </span>
                </div>
              </div>
            </div>

            {/* micro hint */}
            <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.55em] text-brand-ivory/45">
              <span className="inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-brand-gold" />
                {finalMicroLeft}
              </span>
              <span className="inline-flex items-center gap-2">
                <Tag className="w-4 h-4 text-brand-ivory/60" />
                {finalMicroRight}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
